/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useState } from 'react';

import { CreateInstallerTokenResponse, InstallerToken } from '@/client';
import { ContentContainer } from '@/components/ContentContainer';
import { QueryWrapper } from '@/components/QueryWrapper';
import { usePaginatedInstallerTokensQuery } from '@/hooks/query/usePaginatedInstallerTokensQuery';
import { CreateInstallerTokenModal } from '@/pages/InstallerTokens/CreateInstallerTokenModal';
import { InstallerTokenCreatedModal } from '@/pages/InstallerTokens/InstallerTokenCreatedModal';
import { InstallerTokensHeader } from '@/pages/InstallerTokens/InstallerTokensHeader';
import { InstallerTokensTable } from '@/pages/InstallerTokens/InstallerTokensTable';
import { RevokeInstallerTokenModal } from '@/pages/InstallerTokens/RevokeInstallerTokenModal';
import { Container } from '@/primitives/Container';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export function InstallerTokensPage() {
  const tenantID = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createdToken, setCreatedToken] = useState<CreateInstallerTokenResponse | null>(null);
  const [installerToken, setInstallerToken] = useState<InstallerToken | undefined>(undefined);

  const installerTokensQuery = usePaginatedInstallerTokensQuery({
    type: 'queryParams',
  });

  const handleGenerateClick = () => {
    setCreateModalOpen(true);
  };

  const handleCreateModalClose = () => {
    setCreateModalOpen(false);
  };

  const handleTokenCreated = (data: CreateInstallerTokenResponse) => {
    setCreatedToken(data);
  };

  const handleSuccessModalClose = () => {
    setCreatedToken(null);
    // The list will automatically refetch via realtime events (installer-token:created)
  };

  const revokeInstallerToken = (token: InstallerToken) => {
    setInstallerToken(token);
  };

  const closeDeleteDialog = () => {
    setInstallerToken(undefined);
  };

  if (!tenantID) return null;

  return (
    <ContentContainer width="100%">
      <Container full style={{ width: '100%' }}>
        <InstallerTokensHeader onGenerateClick={handleGenerateClick} />
        <QueryWrapper queryResult={installerTokensQuery}>
          {(installerTokensData) => (
            <InstallerTokensTable
              installerTokensData={installerTokensData}
              updateQueryParams={installerTokensQuery.updateQueryParams}
              installerTokenFilters={installerTokensQuery.filters}
              revokeInstallerToken={revokeInstallerToken}
            />
          )}
        </QueryWrapper>
      </Container>

      <CreateInstallerTokenModal
        open={createModalOpen}
        onClose={handleCreateModalClose}
        onSuccess={handleTokenCreated}
      />

      {createdToken && <InstallerTokenCreatedModal createdToken={createdToken} onClose={handleSuccessModalClose} />}

      {installerToken && (
        <RevokeInstallerTokenModal
          installerToken={installerToken}
          onCancelInstallerTokenRevocation={closeDeleteDialog}
        />
      )}
    </ContentContainer>
  );
}
